import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, ExternalLink, FileText, Library, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { memo, useMemo, useRef, useState, type JSX } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isPartnerCitation, type Source } from '../../contracts';
import { COLLECTIONS_MAX_VISIBLE } from '../answer/collectionsDisplay';
import { resolvePartnerSourceSnippetText } from '../answer/partnerDisplaySummary';

interface LibrarySearchBlockProps {
  query: string;
  sources: Source[];
  status: 'searching' | 'completed';
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Single library document item in expanded list - memoized.
 * Shows filename, collection name, snippet preview, and link to library.
 */
const LibrarySourceItem = memo(({ source }: { source: Source }) => {
  const intl = useIntl();
  const title =
    source.title ||
    intl.formatMessage({
      defaultMessage: 'Document',
      id: 'W9D3xHq5Su',
      description: 'Fallback name for a library document without a title',
    });
  // EVI-1570: partner sources show only the plain-text display_summary (never the
  // raw-HTML snippet) — nothing when it's absent. Non-partner sources keep snippet.
  const snippetText = resolvePartnerSourceSnippetText({
    displaySummary: source.display_summary,
    isPartnerSource: isPartnerCitation(source.type),
    fallbackSnippet: source.snippet,
  });

  // Cap visible collection names so a power-user with many collections doesn't
  // blow out the caption width before the CSS `truncate` clips it. The full
  // list is still discoverable in the References section (chips + tooltip).
  const collectionsLabel = useMemo(() => {
    const names = source.collections;
    if (!names?.length) return null;
    if (names.length <= COLLECTIONS_MAX_VISIBLE) return names.join(', ');
    const hidden = names.length - COLLECTIONS_MAX_VISIBLE;
    const more = intl.formatMessage(
      { defaultMessage: '+{count} more', id: '/zFGgPmQSV' },
      { count: hidden }
    );
    return `${names.slice(0, COLLECTIONS_MAX_VISIBLE).join(', ')}, ${more}`;
  }, [source.collections, intl]);

  return (
    <div className="group flex w-full min-w-0 flex-col gap-1 rounded-md px-2 py-1.5 transition-colors hover:bg-surface-hover">
      {/* Header row: icon + filename + collection + link */}
      <div className="flex min-w-0 items-center gap-2">
        <FileText className="size-4 shrink-0 text-text-tertiary" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm text-text-primary">{title}</span>
          {collectionsLabel && (
            <TypographyCaption className="truncate text-text-tertiary">
              <FormattedMessage
                defaultMessage="in {name}"
                id="Oo4A4vyej+"
                values={{ name: collectionsLabel }}
                description="Collection names under a library document, e.g. ‘in Clinical Resources’"
              />
            </TypographyCaption>
          )}
        </div>
        <Link
          href="/library"
          className="flex shrink-0 items-center rounded p-1 text-text-tertiary opacity-0 transition-all hover:bg-surface-hover hover:text-text-primary group-hover:opacity-100"
          title={intl.formatMessage({
            defaultMessage: 'Go to Library',
            id: 'ihrtPkaM9+',
            description: 'Tooltip on the link that opens the document library',
          })}
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="size-3.5" />
        </Link>
      </div>
      {/* Snippet row (if available) */}
      {snippetText && (
        <TypographyCaption className="line-clamp-2 pl-6 text-text-tertiary">
          &ldquo;{snippetText}&rdquo;
        </TypographyCaption>
      )}
    </div>
  );
});

LibrarySourceItem.displayName = 'LibrarySourceItem';

/**
 * Library Search Block - Card-style collapsible library search results
 *
 * Matches the styling of CollapsedSourcesSummary with bg-background-tertiary card.
 * Shows sources from user's uploaded documents with rich metadata.
 */
export const LibrarySearchBlock = ({
  query,
  sources,
  status,
  defaultExpanded = false,
  className,
}: LibrarySearchBlockProps): JSX.Element => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isSearching = status === 'searching';

  // Deduplicate sources by document_id or title
  const uniqueSources = useMemo(() => {
    const seen = new Set<string>();
    return sources.filter((source) => {
      const key = source.document_id || source.title || '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [sources]);

  const sourceCount = uniqueSources.length;

  const scrollState = useVerticalScrollFade(scrollRef, isExpanded, [uniqueSources]);

  // Keyframes for animations
  const animationKeyframes = `
    @keyframes text-shimmer {
      0% { mask-position: 150%; }
      100% { mask-position: -50%; }
    }
  `;

  // Searching state - matches web search style (no card, shows query)
  if (isSearching) {
    return (
      <div
        className={cn(
          'relative w-full min-w-0 overflow-hidden duration-300 animate-in fade-in',
          className
        )}
      >
        <style>{animationKeyframes}</style>
        <div className="flex min-w-0 items-center overflow-hidden p-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            {/* Spinning loader */}
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
            {/* Query text with shimmer effect */}
            <span
              className="w-0 flex-1 truncate text-sm text-text-secondary"
              style={{
                maskImage:
                  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
                maskSize: '200% 100%',
                WebkitMaskImage:
                  'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
                WebkitMaskSize: '200% 100%',
                animation: 'text-shimmer 2s ease-in-out infinite',
              }}
            >
              {query ||
                intl.formatMessage({
                  defaultMessage: 'Searching your library',
                  id: 'pE+yNoMw3x',
                  description: 'Status while a library search runs with no query text',
                })}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no sources
  if (sourceCount === 0) {
    return <></>;
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-xl bg-background-tertiary p-2 duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Header - always visible */}
      <CollapsibleTrigger asChild>
        <button className="flex w-full min-w-0 items-center justify-between overflow-hidden rounded-md p-2 text-left transition-colors hover:bg-surface-hover">
          {/* Left side: Icon + Count + Label */}
          <div className="flex min-w-0 items-center gap-1">
            <div className="flex min-w-0 items-center gap-2">
              <Library size={18} className="shrink-0 text-text-secondary" />
              <TypographyCaptionBold className="text-text-secondary">
                <FormattedMessage
                  defaultMessage="{count, plural, one {# source found:} other {# sources found:}}"
                  id="0w30FTK51B"
                  values={{ count: sourceCount }}
                  description="Source count on a library search block in Evidence chat"
                />
              </TypographyCaptionBold>
            </div>
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Your Library"
                id="DU8Zh/X5e8"
                description="Label naming the user’s document library as the search source"
              />
            </TypographyCaption>
          </div>

          {/* Right side: View + chevron */}
          <div className="flex shrink-0 items-center gap-2">
            <TypographyCaption className="text-text-secondary">
              {isExpanded ? (
                <FormattedMessage
                  defaultMessage="Hide"
                  id="MpAjKuNzuU"
                  description="Collapses the library search results list"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="View"
                  id="/FRZR6sCVf"
                  description="Expands the library search results list"
                />
              )}
            </TypographyCaption>
            <ChevronDown
              className={cn(
                'size-4 text-text-secondary transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </div>
        </button>
      </CollapsibleTrigger>

      {/* Expanded source list */}
      <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div className="relative px-2 pb-2 pt-1">
          {/* Top fade gradient */}
          <div
            className={cn(
              'pointer-events-none absolute inset-x-2 top-1 z-10 h-6 bg-gradient-to-b from-background-tertiary to-transparent transition-opacity duration-200',
              scrollState.canScrollUp ? 'opacity-100' : 'opacity-0'
            )}
          />

          <ScrollArea
            scrollbarVariant="minimal"
            className="max-h-64"
            viewportRef={scrollRef}
            viewportClassName="max-h-64"
          >
            <div className="space-y-0.5">
              {uniqueSources.map((source, idx) => (
                <LibrarySourceItem
                  key={`library-source-${idx}-${source.document_id || source.title}`}
                  source={source}
                />
              ))}
            </div>
          </ScrollArea>

          {/* Bottom fade gradient */}
          <div
            className={cn(
              'pointer-events-none absolute inset-x-2 bottom-2 z-10 h-6 bg-gradient-to-t from-background-tertiary to-transparent transition-opacity duration-200',
              scrollState.canScrollDown ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
